<template>
  <section
    id="booking-wizard"
    class="mx-auto max-w-3xl scroll-mt-24 px-4 py-10 sm:py-14"
  >
    <div class="mb-8 text-center">
      <h2 class="text-2xl font-bold text-highlighted sm:text-3xl">
        {{ title }}
      </h2>
      <p
        v-if="description"
        class="mx-auto mt-3 max-w-xl text-muted"
      >
        {{ description }}
      </p>
    </div>

    <UAlert
      v-if="flow.error"
      color="error"
      variant="subtle"
      :title="flow.error"
      class="mb-4"
      :close="{ color: 'neutral', variant: 'link' }"
      @update:open="flow.error = ''"
    />

    <UCard class="rounded-2xl">
      <div
        v-if="flow.step !== 'success'"
        class="mb-6 flex flex-wrap gap-2 text-xs"
      >
        <UBadge
          v-for="item in flow.stepLabels"
          :key="item.id"
          :color="item.id === flow.step ? 'primary' : 'neutral'"
          :variant="item.id === flow.step ? 'solid' : 'subtle'"
        >
          {{ item.label }}
        </UBadge>
      </div>

      <div
        v-if="flow.loading && flow.step !== 'success'"
        class="space-y-3"
      >
        <USkeleton class="h-16 w-full" />
        <USkeleton class="h-16 w-full" />
        <USkeleton class="h-16 w-full" />
      </div>

      <div
        v-else-if="flow.step === 'service'"
        class="grid gap-3"
      >
        <button
          v-for="service in flow.services"
          :key="service.id"
          type="button"
          class="rounded-xl border p-4 text-start transition hover:border-primary"
          :class="flow.selectedService?.id === service.id ? 'border-primary bg-primary/5' : 'border-default'"
          @click="flow.chooseService(service)"
        >
          <p class="font-semibold">{{ service.title }}</p>
          <p class="mt-1 text-sm text-muted">
            {{ flow.toFaDigits(service.durationMin) }} دقیقه
            · {{ flow.visitTypesLabel(service.visitTypes) }}
            · {{ flow.paymentLabel(service) }}
          </p>
        </button>
        <AppEmptyState
          v-if="!flow.services.length"
          title="خدمت قابل رزروی تعریف نشده"
          description="مالک سایت هنوز خدمتی را برای رزرو آنلاین فعال نکرده است."
        />
      </div>

      <BookingFlowSteps
        v-else
        :flow="flow"
        :success-title="successTitle || 'نوبت شما ثبت شد'"
        :success-description="successDescription || 'پیامک تأیید به شماره شما ارسال می‌شود.'"
      />

      <BookingFlowNav
        v-if="flow.showStepNav"
        :flow="flow"
        class="mt-5 border-t border-default pt-4"
      />
    </UCard>
  </section>
</template>

<script setup lang="ts">
import { useBookingFlow } from '~/composables/useBookingFlow'
import BookingFlowSteps from '~/components/blocks/BookingFlowSteps.vue'
import BookingFlowNav from '~/components/blocks/BookingFlowNav.vue'

defineProps<{
  title?: string
  description?: string
  successTitle?: string
  successDescription?: string
}>()

const route = useRoute()
const router = useRouter()
const flow = useBookingFlow()

/** `?service=` carries the service card id (schema ref) clicked on a services block. */
const preselectRef = computed(() => {
  const value = route.query.service
  return typeof value === 'string' ? value : ''
})

function scrollToWizard() {
  if (!import.meta.client) return
  nextTick(() => {
    document.getElementById('booking-wizard')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

onMounted(async () => {
  const paid = route.query.paid
  const appointmentId = route.query.appointment
  const hasPaymentReturn = paid === '0' || paid === '1'

  async function clearPaymentQuery() {
    const query = { ...route.query }
    delete query.paid
    delete query.appointment
    await router.replace({ path: route.path, query, hash: route.hash })
  }

  if (paid === '1' && typeof appointmentId === 'string' && appointmentId) {
    scrollToWizard()
    await flow.resumeFromPayment(appointmentId)
    await clearPaymentQuery()
    return
  }
  if (paid === '0') {
    flow.markPaymentFailed()
    await clearPaymentQuery()
  }

  if (route.hash === '#booking-wizard' || preselectRef.value || hasPaymentReturn) scrollToWizard()
  await flow.loadServices()
  flow.applyPreselect(preselectRef.value)
})

watch(preselectRef, async (value) => {
  if (!value) return
  flow.reset()
  if (!flow.services.length || flow.paymentGatewayLoadFailed) await flow.loadServices()
  flow.applyPreselect(value)
  scrollToWizard()
})
</script>
